import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';

const STATUS_STYLES = {
  pending:   { bg: 'rgba(245,158,11,0.10)', color: 'var(--accent-a)', label: 'Pendente' },
  enriching: { bg: 'rgba(67,97,238,0.10)',  color: 'var(--accent)',   label: 'Enriquecendo' },
  done:      { bg: 'rgba(6,185,111,0.10)',  color: 'var(--accent-g)', label: 'Enriquecido' },
  error:     { bg: 'rgba(239,68,68,0.10)',  color: 'var(--accent-r)', label: 'Erro' },
};

const GROUP_OPTIONS = [
  { key: '', label: 'Sem agrupar' },
  { key: 'product', label: 'Produto' },
  { key: 'fb_page', label: 'Pagina FB' },
  { key: 'vsl_hash', label: 'VSL (mesma)' },
  { key: 'affiliate', label: 'Afiliado' },
  { key: 'oferta', label: 'Oferta (afiliado + LP)' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'status', label: 'Status' },
  { key: 'display_url', label: 'Display URL' },
  { key: 'manager', label: 'Gestor' },
  { key: 'account', label: 'Conta' },
  { key: 'campaign_stage', label: 'Estagio' },
  { key: 'campaign_type', label: 'Tipo (ABO/CBO)' },
];

const GATEWAY_OPTIONS = [
  { key: '', label: 'Todos' },
  { key: 'digistore', label: 'Digistore' },
  { key: 'buygoods', label: 'BuyGoods' },
  { key: 'hotmart', label: 'Hotmart' },
  { key: 'clickbank', label: 'ClickBank' },
  { key: 'kiwify', label: 'Kiwify' },
  { key: 'monetizze', label: 'Monetizze' },
  { key: 'perfectpay', label: 'PerfectPay' },
  { key: 'ticto', label: 'Ticto' },
  { key: 'kirvano', label: 'Kirvano' },
  { key: 'payt', label: 'Payt' },
  { key: 'braip', label: 'Braip' },
];

const STATUS_OPTIONS = [
  { key: '', label: 'Todos' },
  { key: 'done', label: 'Concluidas' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'enriching', label: 'Enriquecendo' },
  { key: 'error', label: 'Com erro' },
];

const SORT_OPTIONS = [
  { key: 'last_seen', label: 'Visto por ultimo' },
  { key: 'created', label: 'Mais recentes' },
  { key: 'adlib', label: 'Ads ativos' },
  { key: 'vsl_dur', label: 'VSL mais longa' },
  { key: 'n_checkouts', label: '# de checkouts' },
];

const fmtAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

const fmtDur = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

function HistorySparkline({ entryId }) {
  const [hist, setHist] = useState([]);
  useEffect(() => {
    apiFetch(`/api/manual-mining/${entryId}/history?days=30`)
      .then(d => setHist(d.history || []))
      .catch(() => {});
  }, [entryId]);
  if (hist.length < 2) return <span style={{ fontSize: 10, color: 'var(--text3)' }}>sem historico ({hist.length} snapshot{hist.length !== 1 ? 's' : ''})</span>;
  const values = hist.map(h => h.adlib_count_current || 0);
  const max = Math.max(...values, 1);
  const w = 200, h = 40;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - (v / max) * h}`).join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const delta = last - first;
  const color = delta > 0 ? 'var(--accent-g)' : delta < 0 ? 'var(--accent-r)' : 'var(--text3)';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <svg width={w} height={h} style={{ overflow: 'visible' }}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      </svg>
      <span style={{ fontSize: 11, color }}>{delta > 0 ? '+' : ''}{delta} ({hist.length}d)</span>
    </div>
  );
}

function AdsGrid({ entryId }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch(`/api/manual-mining/${entryId}/ads`)
      .then(setAds)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entryId]);
  if (loading) return <div style={s.dim}>Carregando ads...</div>;
  if (!ads.length) return <div style={s.dim}>Nenhum ad individual capturado (LP pode nao ter matches no Ad Library).</div>;
  const active = ads.filter(a => a.is_active);
  const inactive = ads.filter(a => !a.is_active);
  return (
    <div>
      <div style={s.dim}>{active.length} ativos · {inactive.length} inativos (sumiram do Ad Library)</div>
      <div style={s.adsGrid}>
        {ads.map(a => (
          <div key={a.id} style={{ ...s.adCard, opacity: a.is_active ? 1 : 0.5 }}>
            {a.video_local_path ? (
              <video src={a.video_local_path} poster={a.thumbnail_local_path || a.thumbnail_url} controls preload="none" style={s.adThumb} />
            ) : (a.thumbnail_local_path || a.thumbnail_url) ? (
              <img src={a.thumbnail_local_path || a.thumbnail_url} alt="thumb" style={s.adThumb} />
            ) : <div style={s.adThumbPlaceholder}>{a.format === 'video' ? '▶' : '◈'}</div>}
            <div style={s.adCardBody}>
              <div style={s.adCardTop}>
                <span style={s.adFmt}>{a.format || '—'}</span>
                {a.days_active !== null && a.days_active !== undefined && (
                  <span style={s.adDays} title={a.total_active_hours ? `${a.total_active_hours}h totais ativos` : ''}>
                    {a.days_active}d{a.total_active_hours ? ` · ${a.total_active_hours}h` : ''}
                  </span>
                )}
                {a.ads_using_creative > 1 && (
                  <span style={s.adShared} title={`${a.ads_using_creative} ads compartilham este criativo`}>×{a.ads_using_creative}</span>
                )}
                {!a.is_active && <span style={s.adInactive}>off</span>}
              </div>
              <div style={s.adCardText} title={a.creative_text}>{(a.creative_text || '').slice(0, 100)}</div>
              <div style={s.adCardFooter}>
                {(a.platforms || []).map(p => <span key={p} style={s.plat}>{p}</span>)}
                {a.video_local_path && <span style={s.plat} title="video baixado localmente">📥</span>}
                <a href={a.preview_url} target="_blank" rel="noopener" style={s.adCardLink}>ver no Ad Library</a>
              </div>
              {a.started_at && <div style={s.adStarted}>Iniciado {a.started_at}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareModal({ data, onClose }) {
  const entries = data.entries || [];
  const fields = [
    { key: 'product_name', label: 'Produto' },
    { key: 'product_price', label: 'Preço' },
    { key: 'lp_domain', label: 'LP domain' },
    { key: 'display_url_from_fb', label: 'Display URL FB' },
    { key: 'adlib_count_current', label: 'Ads no Ad Library (LP)' },
    { key: 'adlib_count_by_display', label: 'Ads por Display URL' },
    { key: 'vsl_hash', label: 'VSL hash', short: 12 },
    { key: 'vsl_duration_s', label: 'VSL duração (s)' },
    { key: 'checkout_gateway', label: 'Checkout gateway' },
    { key: 'affiliate_id', label: 'Affiliate ID' },
    { key: 'affiliate_hash', label: 'Affiliate hash', short: 12 },
    { key: 'fb_page_name', label: 'Página FB' },
    { key: 'ad_page_id', label: 'FB page ID' },
    { key: 'ad_post_type', label: 'Post type' },
    { key: 'ad_reactions', label: 'Reactions' },
    { key: 'ad_shares', label: 'Shares' },
    { key: 'nicho', label: 'Nicho' },
    { key: 'seen_count', label: 'Seen count' },
    { key: 'enriched_at', label: 'Enriched at', short: 10 },
  ];
  const insightFields = [
    { key: 'stage', label: 'UTM: Estágio' },
    { key: 'campaign_type', label: 'UTM: Tipo' },
    { key: 'structure', label: 'UTM: Estrutura' },
    { key: 'variants', label: 'UTM: Variantes' },
    { key: 'products', label: 'UTM: Produtos' },
    { key: 'audience_age', label: 'UTM: Idade' },
    { key: 'audience_geo', label: 'UTM: Geo' },
    { key: 'placements', label: 'UTM: Placements' },
    { key: 'manager_hints', label: 'UTM: Gestor' },
    { key: 'account_hints', label: 'UTM: Conta' },
  ];
  const getVal = (e, key) => {
    let v = e[key];
    if (v === null || v === undefined || v === '') return null;
    return v;
  };
  const getInsight = (e, key) => {
    const ins = (e.tracking_insights || {}).insights || {};
    return ins[key] ?? null;
  };
  const fmt = (v, short) => {
    if (v === null || v === undefined || v === '') return '—';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    const s = String(v);
    return short && s.length > short ? s.slice(0, short) + '…' : s;
  };
  const diffColor = (values) => {
    const nonNull = values.filter(v => v !== null && v !== undefined && v !== '');
    if (nonNull.length === 0) return 'transparent';
    if (nonNull.length < values.length) return 'rgba(148,163,184,0.10)';  // cinza — falta dado
    const set = new Set(nonNull.map(v => Array.isArray(v) ? JSON.stringify(v.slice().sort()) : String(v)));
    if (set.size === 1) return 'rgba(6,185,111,0.10)';  // verde — todos iguais
    // Numeric — check big divergence
    const nums = nonNull.map(v => typeof v === 'number' ? v : null).filter(x => x !== null);
    if (nums.length === nonNull.length && nums.length >= 2) {
      const min = Math.min(...nums), max = Math.max(...nums);
      if (min > 0 && max / min >= 10) return 'rgba(239,68,68,0.15)';  // vermelho — divergencia grande
      if (min === 0 && max > 0) return 'rgba(239,68,68,0.15)';
    }
    return 'rgba(245,158,11,0.08)';  // laranja — diferente
  };
  const copyAsText = () => {
    const lines = ['=== COMPARAÇÃO ==='];
    lines.push('Campo\t' + entries.map(e => e.fb_page_name || e.lp_domain || e.id.slice(0,8)).join('\t'));
    for (const f of fields) {
      const vals = entries.map(e => fmt(getVal(e, f.key), f.short));
      lines.push(`${f.label}\t${vals.join('\t')}`);
    }
    for (const f of insightFields) {
      const vals = entries.map(e => fmt(getInsight(e, f.key)));
      if (vals.every(v => v === '—')) continue;
      lines.push(`${f.label}\t${vals.join('\t')}`);
    }
    navigator.clipboard.writeText(lines.join('\n'));
    alert('Copiado! Cole numa planilha (tab-separated).');
  };

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modalContent} onClick={ev => ev.stopPropagation()}>
        <div style={s.modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Comparar {entries.length} entries</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={copyAsText} style={s.actBtn}>Copiar como texto</button>
            <button onClick={onClose} style={s.actBtnGhost}>Fechar</button>
          </div>
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={s.cmpTable}>
            <thead>
              <tr>
                <th style={s.cmpTh}>Campo</th>
                {entries.map(e => (
                  <th key={e.id} style={s.cmpTh}>
                    {e.fb_page_name || e.lp_domain || e.id.slice(0,8)}
                    <div style={{ ...s.dim, fontWeight: 400 }}>{e.lp_domain}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => {
                const vals = entries.map(e => getVal(e, f.key));
                const bg = diffColor(vals);
                return (
                  <tr key={f.key} style={{ background: bg }}>
                    <td style={s.cmpTdLabel}>{f.label}</td>
                    {vals.map((v, i) => <td key={i} style={s.cmpTd} title={String(v ?? '')}>{fmt(v, f.short)}</td>)}
                  </tr>
                );
              })}
              <tr><td colSpan={entries.length + 1} style={{ ...s.cmpTdLabel, background: 'var(--surface2)', paddingTop: 10 }}>UTM Insights</td></tr>
              {insightFields.map(f => {
                const vals = entries.map(e => getInsight(e, f.key));
                if (vals.every(v => v === null || v === undefined || (Array.isArray(v) && v.length === 0))) return null;
                const bg = diffColor(vals);
                return (
                  <tr key={f.key} style={{ background: bg }}>
                    <td style={s.cmpTdLabel}>{f.label}</td>
                    {vals.map((v, i) => <td key={i} style={s.cmpTd}>{fmt(v)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--text3)', borderTop: '1px solid var(--border)' }}>
          🟢 verde = todos iguais · 🟠 laranja = diferentes · 🔴 vermelho = divergência grande (10x+) · ⚪ cinza = falta dado em algum
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'dados', label: 'Dados' },
  { key: 'insights', label: 'Insights UTM' },
  { key: 'checkouts', label: 'Checkouts' },
  { key: 'ads', label: 'Ads' },
  { key: 'vsl', label: 'VSL' },
  { key: 'fbmeta', label: 'FB Meta' },
];

const INSIGHT_LABELS = {
  stage: { label: 'Estágio', color: '#f59e0b' },
  campaign_type: { label: 'Tipo', color: '#4361ee' },
  structure: { label: 'Estrutura', color: '#06b96f' },
  dates_hint: { label: 'Datas', color: '#a78bfa' },
  audience_age: { label: 'Idade', color: '#ec4899' },
  audience_geo: { label: 'Geo', color: '#ec4899' },
  placements: { label: 'Placements', color: '#3b82f6' },
  products: { label: 'Produtos', color: '#10b981' },
  variants: { label: 'Variantes', color: '#8b5cf6' },
  account_hints: { label: 'Contas', color: '#6b7280' },
  manager_hints: { label: 'Gestores', color: '#0891b2' },
  budget_hints: { label: 'Budget hint', color: '#f97316' },
  ad_platform_ids: { label: 'Ad IDs', color: '#94a3b8' },
};

function EditableField({ label, value, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  if (editing) {
    return (
      <div style={s.field}>
        <div style={s.fieldLabel}>{label}</div>
        <input value={val} onChange={ev => setVal(ev.target.value)} autoFocus
          onBlur={() => { setEditing(false); if (val !== (value || '')) onSave(val); }}
          onKeyDown={ev => { if (ev.key === 'Enter') ev.target.blur(); if (ev.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
          style={{ width: '100%', padding: '4px 6px', border: '1px solid var(--accent)', borderRadius: 4, background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit' }}
          placeholder={placeholder} />
      </div>
    );
  }
  return (
    <div style={s.field} onClick={() => setEditing(true)} title="Clique pra editar">
      <div style={s.fieldLabel}>{label} ✎</div>
      <div style={{ ...s.fieldValue, cursor: 'pointer', color: value ? 'var(--text)' : 'var(--text3)' }}>{value || placeholder || '—'}</div>
    </div>
  );
}

function ExpansionDetail({ entry: e, onPatch }) {
  const [tab, setTab] = useState('dados');
  const ti = e.tracking_insights || {};
  const insights = ti.insights || {};
  const nInsights = Object.keys(insights).length;
  const badges = {
    dados: null,
    insights: nInsights || null,
    checkouts: e.checkouts_json?.length || null,
    ads: e.adlib_count_current ?? null,
    vsl: e.vsl_hash ? '✓' : null,
    fbmeta: e.ad_reactions ?? null,
  };
  return (
    <div style={s.detail}>
      <div style={s.tabBar}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ ...s.tabBtn, ...(tab === t.key ? s.tabBtnOn : {}) }}>
            {t.label} {badges[t.key] !== null && <span style={s.tabBadge}>{badges[t.key]}</span>}
          </button>
        ))}
      </div>

      {e.error_msg && <div style={s.errBox}>⚠ {e.error_msg}</div>}

      {tab === 'dados' && (
        <div>
          <div style={s.detailGrid}>
            <Field label="Product" value={e.product_name} />
            <Field label="Price" value={e.product_price} />
            <Field label="LP Title" value={e.lp_title} />
            <Field label="LP CTA" value={e.lp_cta_text} />
            <Field label="LP domain" value={e.lp_domain} />
            <Field label="LP URL completa (com UTMs — bypass cloaker)" value={e.real_url} link />
            <Field label="Variant slug" value={e.lp_variant_slug} />
            <EditableField label="fb_page_name" value={e.fb_page_name} placeholder="ex: Wendy Ainsworth" onSave={v => onPatch(e.id, { fb_page_name: v })} />
            <EditableField label="Display URL (FB)" value={e.display_url_from_fb} placeholder="ex: deepvital.online" onSave={v => onPatch(e.id, { display_url_from_fb: v })} />
            <EditableField label="Nicho" value={e.nicho} placeholder="ex: blood sugar" onSave={v => onPatch(e.id, { nicho: v })} />
            <EditableField label="Notas" value={e.notes} placeholder="Observacoes..." onSave={v => onPatch(e.id, { notes: v })} />
            <Field label="Ads ativos por LP" value={e.adlib_count_current !== null && e.adlib_count_current !== undefined ? e.adlib_count_current.toLocaleString() : null} />
            <Field label="Ads ativos por Display URL" value={e.adlib_count_by_display !== null && e.adlib_count_by_display !== undefined ? e.adlib_count_by_display.toLocaleString() : null} />
            <Field label="Seen count" value={e.seen_count} />
            <Field label="First seen" value={e.first_seen_at?.slice(0, 19).replace('T', ' ')} />
            <Field label="Last seen" value={e.last_seen_at?.slice(0, 19).replace('T', ' ')} />
            <Field label="Enriched at" value={e.enriched_at?.slice(0, 19).replace('T', ' ')} />
          </div>
          {e.lp_screenshot_path && (
            <div style={s.detailSection}>
              <div style={s.detailLabel}>LP Screenshot</div>
              <img src={e.lp_screenshot_path} alt="LP" style={s.lpImg} />
            </div>
          )}
        </div>
      )}

      {tab === 'insights' && (
        <div>
          {nInsights === 0 ? (
            <div style={s.dim}>Nenhum insight extraído (real_url sem UTMs ou ainda não enriquecido).</div>
          ) : (
            <>
              <div style={s.insightGrid}>
                {Object.entries(insights).map(([k, v]) => {
                  const cfg = INSIGHT_LABELS[k] || { label: k, color: '#9ca3af' };
                  const values = Array.isArray(v) ? v : [v];
                  return (
                    <div key={k} style={s.insightCard}>
                      <div style={{ ...s.insightLabel, color: cfg.color }}>{cfg.label}</div>
                      <div style={s.insightChips}>
                        {values.map((val, i) => (
                          <span key={i} style={{ ...s.insightChip, background: cfg.color + '22', color: cfg.color }}>{String(val)}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {ti.raw && Object.keys(ti.raw).length > 0 && (
                <div style={s.detailSection}>
                  <div style={s.detailLabel}>UTMs raw (decoded)</div>
                  <div style={s.detailGrid}>
                    {Object.entries(ti.raw).map(([k, v]) => (
                      <Field key={k} label={k} value={v} mono />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'checkouts' && (
        <div>
          {e.checkouts_json && e.checkouts_json.length > 0 ? (
            <table style={s.checkoutTable}>
              <thead>
                <tr>
                  <th style={s.chTh}>Gateway</th>
                  <th style={s.chTh}>Variante</th>
                  <th style={s.chTh}>Product ID</th>
                  <th style={s.chTh}>Afiliado</th>
                  <th style={s.chTh}>URL</th>
                </tr>
              </thead>
              <tbody>
                {e.checkouts_json.map((c, i) => (
                  <tr key={i}>
                    <td style={s.chTd}><span style={s.gateway}>{c.gateway}</span></td>
                    <td style={s.chTd}>{c.variant_hint || <span style={s.dim}>—</span>}</td>
                    <td style={s.chTd}>{c.product_id || <span style={s.dim}>—</span>}</td>
                    <td style={s.chTd}>{c.affiliate || <span style={s.dim}>—</span>}</td>
                    <td style={s.chTd}><a href={c.url} target="_blank" rel="noopener" style={{ color: 'var(--accent)', fontSize: 11, wordBreak: 'break-all' }}>{c.url.slice(0, 80)}...</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div style={s.dim}>Nenhum checkout capturado na LP.</div>}
        </div>
      )}

      {tab === 'ads' && (
        <div>
          <div style={s.dim}>
            Ad Library: <b>{e.adlib_count_current ?? 0}</b> por LP · <b>{e.adlib_count_by_display ?? '—'}</b> por display URL ({e.display_url_from_fb || '—'})
          </div>
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
            <div style={s.detailLabel}>Historico ads ativos (30d)</div>
            <HistorySparkline entryId={e.id} />
          </div>
          {e.status === 'done' && <AdsGrid entryId={e.id} />}
        </div>
      )}

      {tab === 'vsl' && (
        <>
          <div style={s.detailGrid}>
            <Field label="VSL hash" value={e.vsl_hash} mono />
            <Field label="VSL duration" value={e.vsl_duration_s ? `${e.vsl_duration_s}s (${fmtDur(e.vsl_duration_s)})` : null} />
            <Field label="VSL URL" value={e.vsl_url} link />
            <Field label="VSL m3u8" value={e.vsl_m3u8_url} link />
          </div>
          <div style={{ ...s.detailSection, marginTop: 12 }}>
            <VslDownload entry={e} />
          </div>
        </>
      )}

      {tab === 'fbmeta' && (
        <div>
          <div style={s.detailGrid}>
            <Field label="Post type" value={e.ad_post_type} />
            <Field label="Post ID" value={e.ad_post_id} />
            <Field label="Page ID" value={e.ad_page_id} />
            <Field label="Reactions" value={e.ad_reactions} />
            <Field label="Shares" value={e.ad_shares} />
          </div>
          {e.ad_thumbnail_url && (
            <div style={s.detailSection}>
              <div style={s.detailLabel}>Ad thumbnail (og:image)</div>
              <img src={e.ad_thumbnail_url} alt="Ad thumbnail" style={{ ...s.lpImg, maxHeight: 200 }} />
            </div>
          )}
          {e.ad_description && (
            <div style={s.detailSection}>
              <div style={s.detailLabel}>Description (og:description)</div>
              <div style={{ ...s.dim, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{e.ad_description}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ManualMining() {
  const [adLink, setAdLink] = useState('');
  const [realUrl, setRealUrl] = useState('');
  const [fbPageName, setFbPageName] = useState('');
  const [nicho, setNicho] = useState('');
  const [displayUrl, setDisplayUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], groups: null });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [groupBy, setGroupBy] = useState('');
  const [search, setSearch] = useState('');
  const [nichoFilter, setNichoFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState([]);   // array de slugs (multi)
  const [statusFilter, setStatusFilter] = useState([]);     // array de slugs (multi)
  const [facets, setFacets] = useState(null);
  const [hasVideoFilter, setHasVideoFilter] = useState('');       // '' | 'true' | 'false'
  const [hasVslDownFilter, setHasVslDownFilter] = useState('');   // '' | 'true' | 'false'
  const [minDaysActive, setMinDaysActive] = useState('');          // '' | '3' | '7' | '14' | '30'
  const [minAdsSharing, setMinAdsSharing] = useState('');          // '' | '2' | '3' | '5'
  const [expandedId, setExpandedId] = useState(null);
  const [sortKey, setSortKey] = useState('last_seen'); // last_seen | created | adlib | vsl_dur | n_checkouts
  const [pageLimit, setPageLimit] = useState(50);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const pollRef = useRef(null);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 3) next.add(id);
      else alert('Maximo 3 entries por comparacao');
      return next;
    });
  };

  const openCompare = () => {
    if (selectedIds.size < 2) { alert('Selecione pelo menos 2 entries'); return; }
    const ids = Array.from(selectedIds).join(',');
    apiFetch(`/api/manual-mining/compare?ids=${ids}`)
      .then(setCompareData)
      .catch(err => alert('Erro: ' + err.message));
  };

  const load = (q = search) => {
    const params = new URLSearchParams({ limit: pageLimit });
    if (groupBy) params.append('group_by', groupBy);
    if (nichoFilter) params.append('nicho', nichoFilter);
    if (q) params.append('q', q);
    if (gatewayFilter.length) params.append('gateway', gatewayFilter.join(','));
    if (statusFilter.length) params.append('status', statusFilter.join(','));
    if (hasVideoFilter) params.append('has_video', hasVideoFilter);
    if (hasVslDownFilter) params.append('has_vsl_downloaded', hasVslDownFilter);
    if (minDaysActive) params.append('min_days_active', minDaysActive);
    if (minAdsSharing) params.append('min_ads_sharing', minAdsSharing);
    if (sortKey) params.append('sort', sortKey);
    apiFetch(`/api/manual-mining?${params}`)
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    apiFetch('/api/manual-mining/stats/summary').then(setStats).catch(() => {});
    apiFetch('/api/manual-mining/facets').then(setFacets).catch(() => {});
  };

  useEffect(() => { load(); }, [groupBy, nichoFilter, pageLimit, gatewayFilter, statusFilter, hasVideoFilter, hasVslDownFilter, minDaysActive, minAdsSharing, sortKey]);

  // Debounce da search (300ms)
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const patchEntry = (id, patch) => apiFetch(`/api/manual-mining/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then(() => load()).catch(e => alert('Erro: ' + e.message));

  const deleteEntry = (id, name) => {
    if (!confirm(`Deletar "${name || id.slice(0,8)}"? Isso remove tambem os ads capturados.`)) return;
    apiFetch(`/api/manual-mining/${id}`, { method: 'DELETE' })
      .then(() => { setExpandedId(null); load(); })
      .catch(e => alert('Erro: ' + e.message));
  };

  // Auto-refresh se houver enrichments em curso
  useEffect(() => {
    const items = data.items || Object.values(data.groups || {}).flat();
    const hasActive = items.some(e => e.status === 'pending' || e.status === 'enriching');
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data]);

  const submit = (e) => {
    e.preventDefault();
    if (!adLink.trim() || !realUrl.trim()) return;
    setSubmitting(true);
    setError(null);
    apiFetch('/api/manual-mining', {
      method: 'POST',
      body: JSON.stringify({
        ad_link: adLink.trim(),
        real_url: realUrl.trim(),
        fb_page_name: fbPageName.trim() || null,
        nicho: nicho.trim() || null,
        display_url_from_fb: displayUrl.trim() || null,
      }),
    })
      .then(() => { setAdLink(''); setRealUrl(''); setDisplayUrl(''); load(); })
      .catch(e => setError(e.message))
      .finally(() => setSubmitting(false));
  };

  const reEnrich = (id) => {
    apiFetch(`/api/manual-mining/${id}/re-enrich`, { method: 'POST' })
      .then(() => load())
      .catch(e => alert('Erro: ' + e.message));
  };

  const renderEntry = (e) => {
    const st = STATUS_STYLES[e.status] || STATUS_STYLES.pending;
    const isExpanded = expandedId === e.id;
    return (
      <>
        <tr key={e.id} style={s.tr} onClick={() => setExpandedId(isExpanded ? null : e.id)}>
          <td style={s.td}>
            <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelect(e.id)}
              onClick={ev => ev.stopPropagation()}
              style={{ marginRight: 6, cursor: 'pointer' }} title="Selecionar pra comparar" />
            <div style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              <div style={s.product}>{e.product_name || <span style={s.dim}>—</span>}</div>
              <div style={s.dim}>{e.lp_domain} {e.lp_variant_slug ? `· ${e.lp_variant_slug}` : ''}</div>
            </div>
          </td>
          <td style={s.td}>{e.fb_page_name || <span style={s.dim}>—</span>}</td>
          <td style={s.td}>
            {e.vsl_hash ? (
              <span title={e.vsl_hash} style={s.hash}>{e.vsl_hash.slice(0, 8)}{e.vsl_duration_s ? ` · ${fmtDur(e.vsl_duration_s)}` : ''}</span>
            ) : <span style={s.dim}>—</span>}
          </td>
          <td style={s.td}>
            {e.checkout_gateway ? (
              <span style={s.gateway}>{e.checkout_gateway}</span>
            ) : <span style={s.dim}>—</span>}
            {e.affiliate_id && <div style={s.dim} title={e.affiliate_id}>{e.affiliate_id.slice(0, 20)}</div>}
          </td>
          <td style={s.td}>
            {e.adlib_count_current !== null && e.adlib_count_current !== undefined ? (
              <span style={s.adlibCount} title={`Ads ativos no FB Ad Library pra ${e.lp_domain}`}>{e.adlib_count_current.toLocaleString()}</span>
            ) : <span style={s.dim}>—</span>}
            {(() => {
              const lp = e.adlib_count_current || 0;
              const disp = e.adlib_count_by_display || 0;
              if (disp > 0 && (lp === 0 || disp / (lp || 1) >= 10)) {
                return <div style={s.spoof} title={`Display "${e.display_url_from_fb}" tem ${disp} ads vs LP ${lp} = spoof escalado`}>🚨 SPOOF {disp}</div>;
              }
              return null;
            })()}
          </td>
          <td style={s.td}>
            <span style={{ ...s.statusBadge, background: st.bg, color: st.color }}>{st.label}</span>
            {e.seen_count > 1 && <span style={s.seen}>×{e.seen_count}</span>}
          </td>
          <td style={{ ...s.td, color: 'var(--text3)', fontSize: 11 }}>{fmtAgo(e.last_seen_at)}</td>
          <td style={{ ...s.td, textAlign: 'right' }}>
            <a href={e.real_url} target="_blank" rel="noopener" style={s.actLink} onClick={ev => ev.stopPropagation()}>LP</a>
            {e.ad_link && <a href={e.ad_link} target="_blank" rel="noopener" style={{ ...s.actLink, marginLeft: 6 }} onClick={ev => ev.stopPropagation()}>Ad</a>}
            <button onClick={ev => { ev.stopPropagation(); reEnrich(e.id); }} style={{ ...s.actBtn, marginLeft: 6 }} title="Re-enriquecer">↻</button>
            <button onClick={ev => { ev.stopPropagation(); deleteEntry(e.id, e.fb_page_name || e.lp_domain); }} style={{ ...s.actBtn, marginLeft: 6, color: 'var(--accent-r)' }} title="Deletar">×</button>
          </td>
        </tr>
        {isExpanded && (
          <tr>
            <td colSpan={8} style={{ padding: 0, background: 'var(--surface2)' }}>
              <ExpansionDetail entry={e} onPatch={patchEntry} />
            </td>
          </tr>
        )}
      </>
    );
  };

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div>
          <div style={s.title}>Mineração Manual</div>
          <div style={s.subtitle}>Cole ad_link + real_url do bloco de notas. Enriquecimento automatico: produto / VSL / checkout / afiliado. Agrupa igualdades.</div>
          {stats && (
            <div style={s.statsBar}>
              <span style={s.statChip}><b>{stats.total}</b> total</span>
              {stats.done > 0 && <span style={{ ...s.statChip, color: 'var(--accent-g)' }}>✓ {stats.done}</span>}
              {stats.enriching > 0 && <span style={{ ...s.statChip, color: 'var(--accent)' }}>⟳ {stats.enriching} enriquecendo</span>}
              {stats.pending > 0 && <span style={{ ...s.statChip, color: 'var(--accent-a)' }}>⏳ {stats.pending} pendente</span>}
              {stats.error > 0 && <span style={{ ...s.statChip, color: 'var(--accent-r)' }}>⚠ {stats.error} erro</span>}
            </div>
          )}
        </div>
      </div>

      <form style={s.form} onSubmit={submit}>
        <input type="url" placeholder="ad_link (facebook.com/share/... ou /reel/...)" value={adLink} onChange={e => setAdLink(e.target.value)} style={s.input} required />
        <input type="url" placeholder="real_url (LP) — cole URL COMPLETA (com fbclid, golinhats, etc — bypassa cloaker)" value={realUrl} onChange={e => setRealUrl(e.target.value)} style={s.input} required title="Cole a URL exata da barra de endereco APOS clicar no ad. Params como fbclid=... e golinhats=... sao o que faz o cloaker liberar a versao real da LP (nao a white-hat). Sem esses params o backend ve so o site suave (Hotmart em vez de DS24, produto generico)." />
        <input type="text" placeholder="fb_page_name (opcional)" value={fbPageName} onChange={e => setFbPageName(e.target.value)} style={{ ...s.input, maxWidth: 160 }} />
        <input type="text" placeholder="display url FB (ex: deepvital.online)" value={displayUrl} onChange={e => setDisplayUrl(e.target.value)} style={{ ...s.input, maxWidth: 220 }} />
        <input type="text" placeholder="nicho" value={nicho} onChange={e => setNicho(e.target.value)} style={{ ...s.input, maxWidth: 120 }} />
        <button type="submit" disabled={submitting} style={{ ...s.btn, opacity: submitting ? 0.5 : 1 }}>
          {submitting ? '...' : 'Enriquecer'}
        </button>
      </form>
      {error && <div style={s.error}>{error}</div>}

      <div style={s.stickyControls}>
      <div style={s.filterBar}>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Agrupar:</span>
          {GROUP_OPTIONS.map(o => (
            <button key={o.key} onClick={() => setGroupBy(o.key)}
              style={{ ...s.chip, ...(groupBy === o.key ? s.chipOn : {}) }}>{o.label}</button>
          ))}
        </div>
        <div style={s.filterRight}>
          <input type="text" placeholder="Buscar produto/page/domain..." value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()} style={s.search} />
          <input type="text" placeholder="Nicho" value={nichoFilter} onChange={e => setNichoFilter(e.target.value)} style={{ ...s.search, width: 100 }} />
          {(() => {
            const activeCount = [gatewayFilter.length > 0, statusFilter.length > 0, !!hasVideoFilter, !!hasVslDownFilter, !!minDaysActive, !!minAdsSharing].filter(Boolean).length;
            return (
              <button onClick={() => setFiltersOpen(!filtersOpen)} title="Mais filtros"
                      style={{ ...s.chip, ...(activeCount > 0 ? s.chipOn : {}), marginLeft: 8 }}>
                {filtersOpen ? '▾' : '▸'} Filtros {activeCount > 0 && `(${activeCount})`}
              </button>
            );
          })()}
        </div>
      </div>

      {filtersOpen && (<>
      <div style={s.filterBar}>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>Gateway:</span>
          <MultiChipSelect
            options={GATEWAY_OPTIONS.filter(o => o.key).map(o => ({ ...o, count: facets?.facets?.gateway?.[o.key] || 0 }))}
            value={gatewayFilter}
            onChange={setGatewayFilter}
            placeholder="Qualquer" />
          <span style={{ ...s.filterLabel, marginLeft: 12 }}>Status:</span>
          <MultiChipSelect
            options={STATUS_OPTIONS.filter(o => o.key).map(o => ({ ...o, count: facets?.facets?.status?.[o.key] || 0 }))}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Qualquer" />
          <span style={{ ...s.filterLabel, marginLeft: 12 }}>Video baixado:</span>
          <select value={hasVideoFilter} onChange={e => setHasVideoFilter(e.target.value)} style={s.select}>
            <option value="">Todos</option>
            <option value="true">Sim</option>
            <option value="false">Nao</option>
          </select>
          <span style={{ ...s.filterLabel, marginLeft: 12 }}>VSL baixada:</span>
          <select value={hasVslDownFilter} onChange={e => setHasVslDownFilter(e.target.value)} style={s.select}>
            <option value="">Todos</option>
            <option value="true">Sim</option>
            <option value="false">Nao</option>
          </select>
        </div>
        <div style={s.filterRight}>
          <span style={s.filterLabel}>Ordenar:</span>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={s.select}>
            {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div style={s.filterBar}>
        <div style={s.filterGroup}>
          <button
            onClick={() => {
              const isOn = minDaysActive === '7' && minAdsSharing === '2';
              if (isOn) { setMinDaysActive(''); setMinAdsSharing(''); }
              else { setMinDaysActive('7'); setMinAdsSharing('2'); }
            }}
            title="Preset: ads com >=7d ativos + >=2 criativos compartilhando"
            style={{ ...s.chip, ...((minDaysActive === '7' && minAdsSharing === '2') ? s.chipOn : {}) }}>
            Winners (7d+, 2 criativos+)
          </button>
          <span style={{ ...s.filterLabel, marginLeft: 12 }}>Dias ativos min:</span>
          <select value={minDaysActive} onChange={e => setMinDaysActive(e.target.value)} style={s.select}>
            <option value="">Qualquer</option>
            <option value="3">3+</option>
            <option value="7">7+</option>
            <option value="14">14+</option>
            <option value="30">30+</option>
          </select>
          <span style={{ ...s.filterLabel, marginLeft: 12 }}>Criativos compartilhando min:</span>
          <select value={minAdsSharing} onChange={e => setMinAdsSharing(e.target.value)} style={s.select}>
            <option value="">Qualquer</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="5">5+</option>
            <option value="10">10+</option>
          </select>
        </div>
      </div>
      </>)}

      <SavedViews
        state={{ search, nichoFilter, gatewayFilter, statusFilter, hasVideoFilter, hasVslDownFilter, minDaysActive, minAdsSharing, sortKey, groupBy }}
        apply={(v) => {
          setSearch(v.search || '');
          setNichoFilter(v.nichoFilter || '');
          setGatewayFilter(v.gatewayFilter || []);
          setStatusFilter(v.statusFilter || []);
          setHasVideoFilter(v.hasVideoFilter || '');
          setHasVslDownFilter(v.hasVslDownFilter || '');
          setMinDaysActive(v.minDaysActive || '');
          setMinAdsSharing(v.minAdsSharing || '');
          setSortKey(v.sortKey || 'last_seen');
          setGroupBy(v.groupBy || '');
        }} />

      {(gatewayFilter.length || statusFilter.length || hasVideoFilter || hasVslDownFilter || nichoFilter || search || minDaysActive || minAdsSharing) && (
        <div style={{ ...s.filterBar, gap: 6 }}>
          <span style={s.filterLabel}>Filtros ativos:</span>
          {search && <button onClick={() => setSearch('')} style={s.appliedChip}>busca: {search} ×</button>}
          {nichoFilter && <button onClick={() => setNichoFilter('')} style={s.appliedChip}>nicho: {nichoFilter} ×</button>}
          {gatewayFilter.map(g => <button key={g} onClick={() => setGatewayFilter(gatewayFilter.filter(x => x !== g))} style={s.appliedChip}>gw: {g} ×</button>)}
          {statusFilter.map(st => <button key={st} onClick={() => setStatusFilter(statusFilter.filter(x => x !== st))} style={s.appliedChip}>status: {st} ×</button>)}
          {hasVideoFilter && <button onClick={() => setHasVideoFilter('')} style={s.appliedChip}>video: {hasVideoFilter} ×</button>}
          {hasVslDownFilter && <button onClick={() => setHasVslDownFilter('')} style={s.appliedChip}>vsl: {hasVslDownFilter} ×</button>}
          {minDaysActive && <button onClick={() => setMinDaysActive('')} style={s.appliedChip}>dias≥{minDaysActive} ×</button>}
          {minAdsSharing && <button onClick={() => setMinAdsSharing('')} style={s.appliedChip}>criativos≥{minAdsSharing} ×</button>}
          <button onClick={() => { setSearch(''); setNichoFilter(''); setGatewayFilter([]); setStatusFilter([]); setHasVideoFilter(''); setHasVslDownFilter(''); setMinDaysActive(''); setMinAdsSharing(''); }}
                  style={{ ...s.appliedChip, background: 'transparent', color: 'var(--accent-r)', borderColor: 'var(--accent-r)' }}>
            Limpar tudo
          </button>
        </div>
      )}
      </div>{/* /stickyControls */}

      {loading && <div style={s.dim}>Carregando...</div>}

      {!loading && !groupBy && (() => {
        // Ordenacao ja vem do backend via ?sort=. Client-side eh so passthrough.
        const items = data.items || [];
        const hdrSort = (k, label, width) => (
          <th style={{ ...s.th, cursor: 'pointer', ...(width ? { width } : {}), color: sortKey === k ? 'var(--accent)' : 'var(--text3)' }}
              onClick={() => setSortKey(k)} title="Clique pra ordenar">
            {label} {sortKey === k ? '▼' : ''}
          </th>
        );
        return (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Produto / LP</th>
                  <th style={s.th}>Página FB</th>
                  <th style={s.th}>VSL</th>
                  <th style={s.th}>Checkout / Afiliado</th>
                  {hdrSort('adlib', 'Ads ativos', 90)}
                  <th style={{ ...s.th, width: 110 }}>Status</th>
                  {hdrSort('last_seen', 'Visto', 70)}
                  <th style={{ ...s.th, width: 180, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0
                  ? <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>Nenhuma entry ainda. Cole ad_link + real_url acima.</td></tr>
                  : items.map(renderEntry)}
              </tbody>
            </table>
            {items.length >= pageLimit && (
              <div style={{ padding: 12, textAlign: 'center' }}>
                <button onClick={() => setPageLimit(pageLimit + 50)} style={s.actBtnGhost}>
                  Carregar mais 50 (mostrando {items.length})
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {!loading && groupBy && data.groups && (() => {
        // Tabela cruzada: 1 linha por grupo. Colunas alinhadas verticalmente
        // pra escanear metricas entre grupos (padrao dashboards analiticos).
        const groups = Object.entries(data.groups);
        const allKeys = groups.map(([k]) => k);
        const allOpen = allKeys.length > 0 && allKeys.every(k => expandedGroups.has(k));
        const uniq = (arr) => [...new Set(arr.filter(Boolean))];

        // Pre-computa summary por grupo (usado tanto na row quanto no expand)
        const summaries = groups.map(([groupKey, entries]) => {
          const first = entries[0] || {};
          const hint = first.product_name || first.lp_domain || '';
          const humanTitle = (() => {
            switch (groupBy) {
              case 'affiliate':   return first.affiliate_id || (hint ? `sem afiliado · ${hint}` : `sem afiliado · ${groupKey}`);
              case 'oferta':      return `${first.affiliate_id || 'sem-aff'} · ${first.lp_domain || '?'}`;
              case 'vsl_hash':    return first.product_name ? `${first.product_name} (${first.vsl_duration_s || '?'}s)` : groupKey;
              case 'fb_page':     return first.fb_page_name || (hint ? `sem pagina · ${hint}` : groupKey);
              case 'display_url': return first.display_url_from_fb || (hint ? `sem display · ${hint}` : groupKey);
              case 'product':     return first.product_name || (hint ? `sem produto · ${hint}` : groupKey);
              default:            return groupKey;
            }
          })();
          const products = uniq(entries.map(e => e.product_name));
          const gateways = uniq(entries.flatMap(e => (e.checkouts_json || []).map(c => c.gateway)));
          const domains = uniq(entries.map(e => e.lp_domain));
          const totalAdsActive = entries.reduce((s, e) => s + (e.adlib_count_current || 0), 0);
          const perEntryAds = entries.map(e => e.adlib_count_current || 0).filter(n => n > 0);
          const minA = perEntryAds.length ? Math.min(...perEntryAds) : 0;
          const maxA = perEntryAds.length ? Math.max(...perEntryAds) : 0;
          const withVsl = entries.filter(e => e.vsl_hash).length;
          const size = entries.length;
          const scale = size >= 10 ? 'xl' : size >= 5 ? 'lg' : size >= 2 ? 'md' : 'sm';
          return { groupKey, entries, humanTitle, products, gateways, domains, totalAdsActive, minA, maxA, withVsl, size, scale };
        });

        return (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 11, color: 'var(--text3)' }}>
              <button onClick={() => setExpandedGroups(new Set(allOpen ? [] : allKeys))} style={s.chip}>
                {allOpen ? 'Colapsar todos' : 'Expandir todos'}
              </button>
              <span style={{ alignSelf: 'center' }}>{groups.length} grupos · {data.total || 0} entries</span>
            </div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, width: 22 }}></th>
                    <th style={s.th}>Grupo</th>
                    <th style={{ ...s.th, width: 60, textAlign: 'right' }}>Entries</th>
                    <th style={{ ...s.th, width: 180 }}>Produto</th>
                    <th style={{ ...s.th, width: 100 }}>Gateway</th>
                    <th style={{ ...s.th, width: 140 }}>LP(s)</th>
                    <th style={{ ...s.th, width: 130, textAlign: 'right' }}>Ads ativos</th>
                    <th style={{ ...s.th, width: 70, textAlign: 'right' }}>VSL</th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map(({ groupKey, entries, humanTitle, products, gateways, domains, totalAdsActive, minA, maxA, withVsl, size, scale }) => {
                    const isOpen = expandedGroups.has(groupKey);
                    const scaleColor = scale === 'xl' ? 'var(--accent-g)' : scale === 'lg' ? 'var(--accent)' : scale === 'md' ? 'var(--border2)' : 'transparent';
                    const rowOpacity = scale === 'sm' ? 0.7 : 1;
                    const vslPct = size > 0 ? withVsl / size : 0;
                    const vslColor = vslPct === 1 ? 'var(--accent-g)' : vslPct > 0 ? 'var(--accent-a)' : 'var(--text3)';
                    const productsLabel = products.length === 0 ? '—' : products.length <= 2 ? products.join(', ') : `${products.length} produtos`;
                    const domainsLabel = domains.length === 0 ? '—' : domains.length === 1 ? domains[0] : `${domains.length} LPs`;
                    const adsLabel = totalAdsActive === 0 ? '—' : (minA !== maxA ? `${totalAdsActive} (${minA}-${maxA})` : String(totalAdsActive));
                    const toggle = () => {
                      const next = new Set(expandedGroups);
                      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
                      setExpandedGroups(next);
                    };
                    return (
                      <React.Fragment key={groupKey}>
                        <tr style={{ cursor: 'pointer', opacity: rowOpacity, borderLeft: `3px solid ${scaleColor}` }} onClick={toggle}>
                          <td style={{ ...s.td, color: 'var(--text2)', textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</td>
                          <td style={s.td}>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{humanTitle}</div>
                          </td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{size}</td>
                          <td style={{ ...s.td, color: 'var(--text2)' }}>{productsLabel}</td>
                          <td style={{ ...s.td, color: 'var(--text2)' }}>{gateways.length > 0 ? gateways.join('+') : '—'}</td>
                          <td style={{ ...s.td, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={domains.join(', ')}>{domainsLabel}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: totalAdsActive > 0 ? 'var(--accent)' : 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>{adsLabel}</td>
                          <td style={{ ...s.td, textAlign: 'right', color: vslColor, fontVariantNumeric: 'tabular-nums' }}>{withVsl}/{size}</td>
                        </tr>
                        {isOpen && entries.map(e => (
                          <tr key={`${groupKey}-${e.id}`}>
                            <td colSpan={8} style={{ padding: 0, background: 'var(--surface2)' }}>
                              <table style={s.table}>
                                <tbody>{renderEntry(e)}</tbody>
                              </table>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      <div style={s.footer}>{data.total || 0} entries · agrupamento: {groupBy || 'nenhum'}</div>

      {selectedIds.size > 0 && (
        <div style={s.floatingCompare}>
          <span style={{ marginRight: 10 }}>{selectedIds.size} selecionada{selectedIds.size > 1 ? 's' : ''}</span>
          <button onClick={openCompare} style={s.btn} disabled={selectedIds.size < 2}>Comparar</button>
          <button onClick={() => setSelectedIds(new Set())} style={{ ...s.actBtnGhost, marginLeft: 6 }}>Limpar</button>
        </div>
      )}

      {compareData && <CompareModal data={compareData} onClose={() => setCompareData(null)} />}
    </div>
  );
}

function GroupStat({ label, value, color, strong }) {
  // Layout inline compacto: label pequeno + valor. Width auto (nao fixo) —
  // valores curtos (8) ficam apertados; valores longos (4 produtos) esticam.
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 5, padding: '3px 8px',
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
      lineHeight: 1.1,
    }}>
      <span style={{ fontSize: 8, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 11, color: color || 'var(--text)', fontWeight: strong ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{value}</span>
    </div>
  );
}

function MultiChipSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [open]);
  const toggle = (k) => {
    onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  };
  const label = value.length === 0
    ? placeholder
    : value.length <= 2 ? value.join(', ') : `${value.length} selecionados`;
  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(!open)} style={{ ...s.select, minWidth: 120, textAlign: 'left', cursor: 'pointer' }}>
        {label} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 4, zIndex: 100, minWidth: 200, maxHeight: 260, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {options.map(o => (
            <div key={o.key} onClick={() => toggle(o.key)}
                 style={{ padding: '5px 8px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderRadius: 4, background: value.includes(o.key) ? 'rgba(67,97,238,0.15)' : 'transparent' }}>
              <span style={{ color: value.includes(o.key) ? 'var(--accent)' : 'var(--text)' }}>
                {value.includes(o.key) ? '☑' : '☐'} {o.label}
              </span>
              <span style={{ color: 'var(--text3)', fontSize: 10 }}>{o.count ?? 0}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SAVED_VIEWS_KEY = 'adintel:manual-mining:saved-views';

function SavedViews({ state, apply }) {
  const [views, setViews] = useState([]);
  const [newName, setNewName] = useState('');
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_VIEWS_KEY);
      if (raw) setViews(JSON.parse(raw));
    } catch {}
  }, []);

  const persist = (next) => {
    setViews(next);
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(next));
  };

  const save = () => {
    const name = newName.trim();
    if (!name) return;
    const next = [...views.filter(v => v.name !== name), { name, state }];
    persist(next);
    setNewName('');
    setShowSave(false);
  };

  const remove = (name) => {
    if (!confirm(`Remover view "${name}"?`)) return;
    persist(views.filter(v => v.name !== name));
  };

  return (
    <div style={s.filterBar}>
      <div style={s.filterGroup}>
        <span style={s.filterLabel}>Visoes salvas:</span>
        {views.length === 0 && <span style={{ ...s.filterLabel, fontStyle: 'italic' }}>nenhuma</span>}
        {views.map(v => (
          <span key={v.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <button onClick={() => apply(v.state)} style={{ ...s.chip }} title="Aplicar">{v.name}</button>
            <button onClick={() => remove(v.name)} style={{ ...s.chip, padding: '4px 6px', color: 'var(--accent-r)' }} title="Remover">×</button>
          </span>
        ))}
      </div>
      <div style={s.filterRight}>
        {showSave ? (
          <>
            <input autoFocus type="text" placeholder="Nome da view..." value={newName}
                   onChange={e => setNewName(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && save()}
                   style={{ ...s.search, width: 160 }} />
            <button onClick={save} style={{ ...s.chip, ...s.chipOn }}>Salvar</button>
            <button onClick={() => { setShowSave(false); setNewName(''); }} style={s.chip}>Cancelar</button>
          </>
        ) : (
          <button onClick={() => setShowSave(true)} style={s.chip}>+ Salvar visao atual</button>
        )}
      </div>
    </div>
  );
}

function VslDownload({ entry }) {
  const [status, setStatus] = useState(null); // null | 'scheduled' | 'downloading' | 'ready' | 'error'
  const [err, setErr] = useState(null);
  const [path, setPath] = useState(entry.vsl_downloaded_path);

  useEffect(() => { setPath(entry.vsl_downloaded_path); }, [entry.id, entry.vsl_downloaded_path]);

  // Poll ate ficar pronto quando scheduled/downloading
  useEffect(() => {
    if (status !== 'downloading') return;
    const iv = setInterval(async () => {
      try {
        const d = await apiFetch(`/api/manual-mining/${entry.id}`);
        if (d.vsl_downloaded_path) {
          setPath(d.vsl_downloaded_path);
          setStatus('ready');
          clearInterval(iv);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [status, entry.id]);

  const trigger = async () => {
    setErr(null);
    setStatus('scheduled');
    try {
      const d = await apiFetch(`/api/manual-mining/${entry.id}/download-vsl`, { method: 'POST' });
      if (d.status === 'already_downloaded') {
        setPath(d.vsl_downloaded_path);
        setStatus('ready');
      } else {
        setStatus('downloading');
      }
    } catch (e) {
      setErr(String(e.message || e)); setStatus('error');
    }
  };

  if (path) {
    const filename = String(path).split('/').pop();
    // servidor serve /uploads/ como static; vsl_downloaded_path costuma vir /opt/adintel/server/uploads/...
    // container salva em /app/uploads/... ; servidor monta em /uploads/
    const url = String(path).replace(/^(\/app|\/opt\/adintel\/server)/, '');
    return (
      <div>
        <div style={s.detailLabel}>VSL em alta qualidade</div>
        <video src={url} controls style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 6, border: '1px solid var(--border)' }} />
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <a href={url} download={filename} style={{ ...s.btn, background: 'var(--accent-g)', textDecoration: 'none', display: 'inline-block' }}>Baixar MP4</a>
          <button onClick={trigger} style={{ ...s.btn, background: 'var(--surface2)', color: 'var(--text2)' }} disabled={status === 'downloading'}>Re-baixar</button>
          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{filename}</span>
        </div>
        {err && <div style={{ ...s.errBox, marginTop: 8 }}>{err}</div>}
      </div>
    );
  }

  const disabled = !entry.vsl_m3u8_url || status === 'scheduled' || status === 'downloading';
  const label = status === 'scheduled' ? 'Agendando...'
              : status === 'downloading' ? 'Baixando (aguarde ~1-3min)...'
              : 'Baixar VSL em alta qualidade';
  return (
    <div>
      <div style={s.detailLabel}>VSL — download</div>
      <button onClick={trigger} disabled={disabled}
              style={{ ...s.btn, opacity: disabled ? 0.5 : 1 }}>{label}</button>
      {!entry.vsl_m3u8_url && (
        <div style={{ ...s.fieldValue, marginTop: 6, color: 'var(--text3)' }}>Sem vsl_m3u8_url — enriqueca a entry primeiro.</div>
      )}
      {err && <div style={{ ...s.errBox, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

function Field({ label, value, link, mono }) {
  if (!value && value !== 0) return null;
  const wrapStyle = (mono || link)
    ? { whiteSpace: 'normal', wordBreak: 'break-all', overflow: 'visible', textOverflow: 'clip' }
    : {};
  return (
    <div style={s.field}>
      <div style={s.fieldLabel}>{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noopener"
           style={{ ...s.fieldValue, ...wrapStyle, color: 'var(--accent)', fontSize: 11 }}
           title={value}>{String(value)}</a>
      ) : (
        <div style={{ ...s.fieldValue, ...wrapStyle, ...(mono ? { fontFamily: 'monospace', fontSize: 10 } : {}) }}
             title={String(value)}>{String(value)}</div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '24px', maxWidth: 1500, margin: '0 auto' },
  topBar: { marginBottom: 16 },
  title: { fontSize: 13, fontWeight: 700 },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },

  form: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  input: { flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit', outline: 'none', minWidth: 0 },
  btn: { padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  error: { padding: '8px 12px', background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', borderRadius: 6, fontSize: 12, marginBottom: 12 },

  stickyControls: {
    position: 'sticky', top: 0, zIndex: 10,
    background: 'var(--bg)', paddingTop: 8, paddingBottom: 8,
    borderBottom: '1px solid var(--border)',
    marginBottom: 8,
  },
  filterBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' },
  filterGroup: { display: 'flex', gap: 4, alignItems: 'center' },
  filterLabel: { fontSize: 11, color: 'var(--text3)', marginRight: 6 },
  filterRight: { display: 'flex', gap: 8 },
  chip: { padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text3)', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' },
  chipOn: { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  select: { padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer' },
  appliedChip: { padding: '3px 8px', border: '1px solid var(--accent)', borderRadius: 12, background: 'rgba(67,97,238,0.10)', color: 'var(--accent)', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  search: { padding: '4px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, fontFamily: 'inherit', outline: 'none', width: 200 },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)', fontWeight: 600, whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', borderBottom: '1px solid var(--surface3)', verticalAlign: 'middle', minWidth: 0 },
  tr: { cursor: 'pointer' },

  product: { fontWeight: 600, color: 'var(--text)', fontSize: 12, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dim: { color: 'var(--text3)', fontSize: 11 },
  hash: { fontFamily: 'monospace', fontSize: 11, color: 'var(--accent-p)', background: 'rgba(167,139,250,0.10)', padding: '2px 6px', borderRadius: 4 },
  gateway: { padding: '2px 8px', background: 'rgba(6,185,111,0.10)', color: 'var(--accent-g)', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
  statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  seen: { marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--accent-a)' },
  adlibCount: { fontWeight: 700, fontSize: 13, color: 'var(--accent-g)' },
  adsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginTop: 8 },
  adCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  adThumb: { width: '100%', height: 140, objectFit: 'cover', background: 'var(--surface3)' },
  adThumbPlaceholder: { width: '100%', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: 'var(--text3)', background: 'var(--surface3)' },
  adCardBody: { padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  adCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  adFmt: { padding: '2px 6px', borderRadius: 4, background: 'rgba(67,97,238,0.10)', color: 'var(--accent)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' },
  adDays: { padding: '2px 6px', borderRadius: 4, background: 'rgba(6,185,111,0.10)', color: 'var(--accent-g)', fontSize: 11, fontWeight: 700 },
  adShared: { padding: '2px 6px', borderRadius: 4, background: 'rgba(167,139,250,0.10)', color: 'var(--accent-p)', fontSize: 10, fontWeight: 700 },
  tabBar: { display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 8 },
  tabBtn: { padding: '6px 14px', border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text3)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
  tabBtnOn: { background: 'var(--accent)', color: '#fff' },
  tabBadge: { fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'rgba(255,255,255,0.20)', color: 'inherit' },
  insightGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginBottom: 16 },
  insightCard: { padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 },
  insightLabel: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 },
  insightChips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  insightChip: { padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  statsBar: { display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' },
  statChip: { fontSize: 11, padding: '3px 8px', borderRadius: 4, background: 'var(--surface2)', border: '1px solid var(--border)' },
  spoof: { marginTop: 4, fontSize: 10, fontWeight: 700, color: 'var(--accent-r)', background: 'rgba(239,68,68,0.10)', padding: '2px 5px', borderRadius: 3, display: 'inline-block' },
  floatingCompare: { position: 'fixed', bottom: 24, right: 24, background: 'var(--surface)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8, zIndex: 100, fontSize: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalContent: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, maxWidth: 1200, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader: { padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' },
  cmpTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  cmpTh: { textAlign: 'left', padding: '10px 12px', fontSize: 11, color: 'var(--text2)', borderBottom: '1px solid var(--border)', fontWeight: 700, position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 },
  cmpTdLabel: { padding: '8px 12px', fontSize: 11, color: 'var(--text3)', borderBottom: '1px solid var(--surface3)', fontWeight: 600, width: 200 },
  cmpTd: { padding: '8px 12px', fontSize: 12, color: 'var(--text)', borderBottom: '1px solid var(--surface3)', wordBreak: 'break-all', verticalAlign: 'top' },
  adInactive: { fontSize: 10, color: 'var(--accent-r)', fontWeight: 600 },
  adCardText: { fontSize: 11, color: 'var(--text2)', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', minWidth: 0 },
  adCardFooter: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 },
  adCardLink: { fontSize: 10, color: 'var(--accent)', textDecoration: 'none', marginLeft: 'auto' },
  adStarted: { fontSize: 10, color: 'var(--text3)' },
  plat: { fontSize: 9, padding: '1px 5px', background: 'var(--surface3)', borderRadius: 3, color: 'var(--text3)', textTransform: 'uppercase' },

  actLink: { padding: '4px 10px', background: 'transparent', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, fontWeight: 500, textDecoration: 'none' },
  actBtn: { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },

  detail: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 },
  field: { padding: '8px 10px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 },
  fieldLabel: { fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  fieldValue: { fontSize: 12, color: 'var(--text)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  detailSection: {},
  detailLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 },
  lpImg: { maxWidth: '100%', maxHeight: 400, borderRadius: 6, border: '1px solid var(--border)' },
  errBox: { padding: 10, background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', borderRadius: 6, fontSize: 12 },

  groupList: { display: 'flex', flexDirection: 'column', gap: 12 },
  group: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' },
  groupHeader: { padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface2)' },
  groupTitle: { fontWeight: 700, fontSize: 12, color: 'var(--text)' },
  groupCount: { fontSize: 11, color: 'var(--text3)' },

  checkoutTable: { width: '100%', borderCollapse: 'collapse', fontSize: 11, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 },
  chTh: { textAlign: 'left', padding: '6px 10px', fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)', fontWeight: 600 },
  chTd: { padding: '6px 10px', borderBottom: '1px solid var(--surface3)', fontSize: 11 },

  footer: { marginTop: 8, fontSize: 11, color: 'var(--text3)', textAlign: 'right' },
};
